import type { ApiErrorBody, AuthTokens } from '@tcrd/shared';
import { apiBase, env } from './env';
import { sessionStorageSecure } from './session-storage';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(authenticated: boolean) => void>();

export const tokens = {
  get access() {
    return accessToken;
  },
  set(t: AuthTokens) {
    accessToken = t.accessToken;
    void sessionStorageSecure.setRefresh(t.refreshToken);
  },
  async clear() {
    accessToken = null;
    await sessionStorageSecure.clear();
    listeners.forEach((l) => l(false));
  },
  onChange(fn: (authenticated: boolean) => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

function baseHeaders(): Record<string, string> {
  const h: Record<string, string> = {
    'X-Tenant-Slug': env.tenantSlug,
    'X-Requested-With': 'tcrd',
  };
  if (env.isNative) h['X-Client'] = 'mobile';
  return h;
}

/** Intercambia el refresh token por uno nuevo (una sola petición en vuelo). */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const stored = await sessionStorageSecure.getRefresh();
      if (env.isNative && !stored) return false;
      const res = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...baseHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(stored ? { refreshToken: stored } : {}),
      });
      if (!res.ok) {
        await tokens.clear();
        return false;
      }
      tokens.set((await res.json()) as AuthTokens);
      return true;
    } catch {
      // Sin conexión: conservar la sesión local para reintentar luego.
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  form?: FormData;
  idempotencyKey?: string;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function api<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const headers = baseHeaders();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  const qs = opts.query
    ? '?' +
      new URLSearchParams(
        Object.entries(opts.query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : '';

  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}${qs}`, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'NETWORK_ERROR', 'Sin conexión con el servidor. Verifica tu Internet.');
  }

  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    if (await refreshSession()) return api<T>(path, opts, true);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, err.code ?? 'ERROR', err.message ?? 'Ocurrió un error', err.details);
  }
  return data as T;
}

export const newIdempotencyKey = () => crypto.randomUUID();

/** Mensaje legible para mostrar al usuario. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VALIDATION_ERROR' && Array.isArray(e.details)) return (e.details as string[]).join('. ');
    return e.message;
  }
  return 'Ocurrió un error inesperado';
}
